import {useState} from 'react';
import {assignInlineVars} from '@vanilla-extract/dynamic';

import {useUpdateViewedResultsHistory} from '#apollo/schema';
import {Button, Modal, Text} from '#component/base';
import {ModalClose} from '#component/base/Modal';
import {Transition} from '#component/util';
import {sprinkles} from '#style/sprinkles';

import * as css from './HistoryWelcomeModal.css';

const steps = [
  {
    imgSmall: '/image/history/step-1-small-328x189.svg',
    imgMedium: '/image/history/step-1-medium-457x264.svg',
    imgLarge: '/image/history/step-1-large-403x140.svg',
    headline: 'TallyAge™ Delta and Lifestyle Score are connected',
    lede: 'Your Lifestyle Score and TallyAge™ Delta are linked over time, affected by your daily choices and behaviors. We help track your progress and identify impactful changes to lower your epigenetic age.',
  },
  {
    imgSmall: '/image/history/step-2-small-328x189.svg',
    imgMedium: '/image/history/step-2-medium-457x264.svg',
    imgLarge: '/image/history/step-2-large-403x140.svg',
    headline: 'What is a TallyAge™ Delta?',
    lede: "TallyAge™ Delta is the difference between your epigenetic age and actual age, reflecting how your lifestyle impacts your aging process. Tracking your Delta can provide insights into how you're aging on an epigenetic level.",
  },
  {
    imgSmall: '/image/history/step-3-small-328x189.svg',
    imgMedium: '/image/history/step-3-medium-457x264.svg',
    imgLarge: '/image/history/step-3-large-403x140.svg',
    headline: 'Result fluctuations are normal',
    lede: 'Your TallyAge™ test results may move up and down due to various factors; temporary events like sickness or a life change can influence Lifestyle Score and TallyAge™. Track your overall trend direction over time to gauge your progress.',
  },
];

/**
 * `HistoryWelcomeModal` renders a collection of slides for the user to advance
 * through that introduces the Test History feature. It is intended to be shown
 * the first time the user navigates to the relevant screen.
 */
export const HistoryWelcomeModal = ({hasViewedResultsHistory = false}) => {
  const [updateViewedResultsHistory] = useUpdateViewedResultsHistory();
  const [stepIndex, setStepIndex] = useState(0);
  const isLastStep = stepIndex === steps.length - 1;

  const onAdvanceStep = () => {
    const isNextStepLastStep = stepIndex + 1 === steps.length - 1;

    setStepIndex(currIndex => currIndex + 1);

    if (isNextStepLastStep && !hasViewedResultsHistory) {
      updateViewedResultsHistory();
    }
  };

  return (
    <Modal
      header={
        <Text
          className={sprinkles({height: 'full', flex: 'flex', items: 'center'})}
          color="deepBurgundy"
          variant="heading4">{`${stepIndex + 1} of 3`}</Text>
      }
      footer={
        <Button
          as={isLastStep ? ModalClose : 'button'}
          key={0}
          onClick={isLastStep ? undefined : onAdvanceStep}
          variant={isLastStep ? 'primary' : 'secondary'}>
          {isLastStep ? 'Got it' : 'Next'}
        </Button>
      }
      layout="drawer">
      <div aria-live="polite" className={css.container} role="region">
        {steps.map(({headline, lede, imgSmall, imgMedium, imgLarge}, i) => (
          <Transition appear className={css.step} key={i} show={i === stepIndex}>
            <div
              aria-hidden
              className={css.stepImage}
              style={assignInlineVars({
                [css.smallImage]: `url(${imgSmall})`,
                [css.mediumImage]: `url(${imgMedium})`,
                [css.largeImage]: `url(${imgLarge})`,
              })}
            />

            <Text as="h1" color="burgundy" fontWeight="light" variant="heading2">
              {headline}
            </Text>

            <Text as={typeof lede === 'string' ? 'p' : 'div'} color="mineShaft">
              {lede}
            </Text>
          </Transition>
        ))}
      </div>
    </Modal>
  );
};
